import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getConnections, User } from '../../services/userService'; // Import service and type
import LoadingSpinner from '../common/LoadingSpinner'; // Import spinner
import ErrorAlert from '../common/ErrorAlert'; // Import error display

interface ConnectionsListProps {
    // Keep the connectionsCount prop for the header/empty state logic if needed
    connectionsCount: number; 
}

// Remove mock data
// const mockConnections = [ ... ];

const ConnectionsList: React.FC<ConnectionsListProps> = ({ connectionsCount }) => {
    // Fetch connections using useQuery
    const { 
        data: connections, 
        isLoading, 
        error 
    } = useQuery<User[], Error>(
        // Use a specific key to avoid conflicts with the main connections page query
        'profileConnections', 
        // Call getConnections without search term to get all
        () => getConnections(''), 
        {
            staleTime: 5 * 60 * 1000, // Keep data fresh for 5 mins
            enabled: connectionsCount > 0 // Only fetch if the count from parent indicates there *might* be connections
        }
    );

    // Helper to generate initials 
    const getInitials = (name: string | undefined): string => {
        if (!name) return '?';
        const names = name.split(' ');
        if (names.length === 1) return names[0][0].toUpperCase();
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    };

    // Determine the display state based on fetch status and data
    const displayConnections = connections || [];
    const hasConnections = displayConnections.length > 0;

    return (
        <div className="card border-0 shadow-sm mt-4"> {/* Added margin-top */} 
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-bold">Connections ({connectionsCount})</h5>
                {/* Link to main connections page */} 
                <Link to="/connections" className="text-decoration-none small">View All</Link>
            </div>
            <div className="card-body p-3">
                {isLoading ? (
                    <div className="text-center py-3">
                        <LoadingSpinner size="sm" text="Loading connections..." />
                    </div>
                ) : error ? (
                    <ErrorAlert message={(error as Error).message || 'Could not load connections.'} />
                ) : hasConnections ? (
                    // Map through *fetched* connections data
                    displayConnections.slice(0, 5).map(conn => ( // Limit to first 5 for profile preview
                        <Link key={conn.id} to={`/profile/${conn.id}`} className="text-decoration-none text-dark d-block mb-2"> {/* Added mb-2 */} 
                            <div className="d-flex align-items-center p-2 rounded hover-bg-light" style={{ cursor: 'pointer' }}>
                                <div 
                                    className={`rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-3`}
                                    style={{ width: '40px', height: '40px' }}
                                >
                                    {/* Generate initials from actual name */} 
                                    <span className="fw-bold">{getInitials(conn.name)}</span>
                                </div>
                                <div>
                                    <h6 className="mb-0 fw-bold">{conn.name}</h6>
                                    {/* Display profession and company if available */} 
                                    <p className="text-muted small mb-0">
                                        {conn.profession || 'No profession specified'}
                                        {conn.company ? ` at ${conn.company}` : ''}
                                    </p>
                                </div>
                            </div>
                        </Link>
                    ))
                ) : (
                    // Empty State Content (keep as is)
                    <div className="text-center py-3">
                        <div className="mb-3">
                            <i className="fas fa-user-friends fa-3x text-muted"></i> {/* Use FontAwesome icon */}
                        </div>
                        <h6 className="mb-2">No connections yet</h6>
                        <p className="text-muted small mb-3">Start exploring the map to find data professionals near you</p>
                        <Link to="/map" className="btn btn-primary rounded-pill">
                            <i className="fas fa-map-marker-alt me-2"></i> Explore Map {/* Use FontAwesome icon */}
                        </Link>
                    </div>
                )}
                 {/* Show "View All" link inside card body if more than 5 connections exist */} 
                 {displayConnections.length > 5 && (
                    <div className="text-center mt-2">
                        <Link to="/connections" className="text-decoration-none small">View All ({connectionsCount}) Connections</Link>
                    </div>
                 )}
            </div>
        </div>
    );
};

export default ConnectionsList; 